import { useQuery } from '@apollo/client/react';
import { GET_CARD_LATEST_TRANSACTION_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { CardLatestTransactionResponse } from '../../integrations/allkort/graphql/interface/Customer';

type UseGetCardLatestTransactionOptions = {
    skip?: boolean;
    fetchPolicy?: 'cache-first' | 'network-only' | 'cache-and-network' | 'no-cache';
};

/**
 * useGetCardLatestTransaction
 *
 * Fetches the most recent transaction (at + amount) for a SINGLE card by id.
 * Used for the "senast använd" (last used) column in the card list view.
 *
 * The query is where-filtered on card id, so it returns at most 1 card with
 * at most 1 transaction — minimal API field cost.
 *
 * @param cardId - Numeric card id (e.g. parseInt(unifiedCard.id))
 * @param options.skip - Skip the query entirely (e.g. when cardId is unknown)
 *
 * @example
 * ```tsx
 * const { latestTransaction, loading } = useGetCardLatestTransaction(parseInt(card.id));
 * const lastUsed = latestTransaction ? new Date(latestTransaction.at).toLocaleDateString('sv-SE') : 'Aldrig använd';
 * ```
 */
export const useGetCardLatestTransaction = (
    cardId: number | null | undefined,
    options: UseGetCardLatestTransactionOptions = {}
) => {
    const skip = options.skip ?? (cardId === null || cardId === undefined || Number.isNaN(cardId));

    const { data, loading, error } = useQuery<CardLatestTransactionResponse>(
        GET_CARD_LATEST_TRANSACTION_QUERY,
        {
            variables: { after: null, before: null, first: null, last: 1, id: cardId as number },
            skip,
            fetchPolicy: options.fetchPolicy || 'cache-first',
            errorPolicy: 'all',
        }
    );

    // Verify the returned card is the one requested (id check), then grab its latest transaction
    const node = data?.currentCustomer?.cards?.nodes?.find(card => String(card.id) === String(cardId));
    const latestTransaction = node?.transactions?.nodes?.[0] ?? null;

    return { latestTransaction, loading: skip ? false : loading, error };
};

